import { Colors } from "../../../utils/colors";
import {
  MatchResponsePayload,
  Score,
  TeamWithScores,
} from "../../../apis/types";
import { TeamPlayer } from "./TeamPlayer";
import { useMemo, useState } from "react";
import { useFontDesk } from "../../../context/frontDeskContext";
import { MatchCardHeader } from "./MatchCardHeader";
import { Button } from "../../../components/button/Button";
import { PlayerDetailsList } from "./PlayerDetailsList";
import { TCText } from "../../../components/tcText/TCText";

type props = {
  match: MatchResponsePayload;
};

export const MatchCard: React.FC<props> = ({ match }) => {
  const { staffModel } = useFontDesk();
  const [copied, setCopied] = useState(false);

  const currentTeam = useMemo(() => {
    if (
      match.teamA.playerA.playerId.toString() ===
        staffModel?.userId.toString() ||
      match.teamA.playerB.playerId.toString() === staffModel?.userId.toString()
    ) {
      return match.teamA;
    } else {
      return match.teamB;
    }
  }, [match.teamA, match.teamB, staffModel?.userId]);

  const currentTeamScores = useMemo<Score[]>(() => {
    return match.scores.filter((score) => score.teamId === currentTeam.id);
  }, [currentTeam, match.scores]);

  const opponentTeamScores = useMemo<Score[]>(() => {
    return match.scores.filter((score) => score.teamId !== currentTeam.id);
  }, [currentTeam, match.scores]);

  const noOfSetsWon = useMemo<[number, number]>(() => {
    let currentTeamCount = 0;
    let opponentTeamCount = 0;
    for (let i = 0; i < currentTeamScores.length; i++) {
      if (currentTeamScores[i].score > opponentTeamScores[i].score) {
        currentTeamCount++;
      }
      if (opponentTeamScores[i].score > currentTeamScores[i].score) {
        opponentTeamCount++;
      }
    }
    return [currentTeamCount, opponentTeamCount];
  }, [currentTeamScores, opponentTeamScores]);

  const currentTeamWithScores = useMemo<TeamWithScores>(() => {
    return {
      ...currentTeam,
      scores: currentTeamScores,
      noOfSetsWon: noOfSetsWon[0],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeam, match.scores, noOfSetsWon]);

  const opponentTeamWithScores = useMemo<TeamWithScores>(() => {
    const opponentTeam =
      currentTeam.id === match.teamA.id ? match.teamB : match.teamA;
    return {
      ...opponentTeam,
      scores: opponentTeamScores,
      noOfSetsWon: noOfSetsWon[1],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeam, match.scores, noOfSetsWon]);

  const { isMobile } = useFontDesk();

  return (
    <div>
      <div
        style={{
          width: "100%",
          paddingTop: 5,
          paddingLeft: 5,
          paddingRight: 5,
          paddingBottom: 16,
          backgroundColor: Colors.white,
          borderTopRightRadius: 20,
          borderTopLeftRadius: 20,
          borderBottomLeftRadius: match.externalIdUrl ? 0 : 20,
          borderBottomRightRadius: match.externalIdUrl ? 0 : 20,
        }}>
        <MatchCardHeader matchDate={match.date} courtName={match.courtName} />
        <div
          style={{
            display: "flex",
            width: "100%",
            alignItems: "center",
          }}>
          <div
            style={{
              height: 1,
              backgroundColor: Colors.lightGreyBackground,
              flex: 1,
            }}
          />
          <TCText
            value={match.isScoreAdded ? "Scores added" : "No scores added"}
            style={{
              fontWeight: 600,
              fontSize: 12,
              color: Colors.green,
              textAlign: "center",
            }}
          />
          <div
            style={{
              height: 1,
              backgroundColor: Colors.lightGreyBackground,
              flex: 1,
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 50,
          }}>
          <div
            style={{
              height: "100%",
              width: "100%",
              paddingLeft: 10,
              paddingRight: 10,
              marginTop: 10,
              flex: 1,
            }}>
            <TeamPlayer
              isCurrentUserTeam
              team={currentTeamWithScores}
              isAnyScoreAdded={match.isScoreAdded}
            />
            <TeamPlayer
              team={opponentTeamWithScores}
              isAnyScoreAdded={match.isScoreAdded}
            />
          </div>
        </div>

        {isMobile ? (
          <div
            style={{
              height: 1,
              backgroundColor: Colors.lightGreyBackground,
              flex: 1,
              marginTop: 15,
            }}
          />
        ) : null}
        {isMobile ? <PlayerDetailsList match={match} /> : null}
      </div>

      {match.externalIdUrl ? (
        <Button
          disabled={copied}
          btnStyle={{
            backgroundColor: Colors.darkBrown,
            width: "100%",
            borderRadius: "0 0 16px 16px",
            alignSelf: "center",
          }}
          textStyle={{ color: Colors.white }}
          text={copied ? "✓" : "Copy URL"}
          onPress={() => {
            navigator.clipboard.writeText(match.externalIdUrl ?? "");
            setCopied(true);
            setTimeout(() => {
              setCopied(false);
            }, 3000);
          }}
        />
      ) : null}
    </div>
  );
};
